<script setup>
import { ref } from 'vue'

import { getI18n } from '@/plugins/i18n'
import { DxDataGrid } from 'devextreme-vue/data-grid'
import { useGridRef, useODataSource, useDefaultGridOptions, refreshGridAsync } from '@/composables/data-grid'
import { useDataGridWidget } from '@/composables/data-grid-toolbar'
import { useDetectDevice } from '@/composables/device'
import { useCurrency } from '@/composables/currency'
import { formatDuration, reportPeriodLabel, useReportSubject } from '@/composables/report'
import { signalToneEnum, signalToneColor } from '@/composables/dashboard-kpi'

import { useAccountingStore } from '@/stores/accounting.store'
import { useWebSiteStore } from '@/stores/web-site.store'

import ChipTemplate from '@/components/shared/ChipTemplate.vue'
import ConfirmDialog from '@/components/shared/ConfirmDialog.vue'
import ReportEdit from '@/components/accounting/ReportEdit.vue'
import ReportFileDialog from '@/components/accounting/ReportFileDialog.vue'
import ReportDetailPanel from '@/components/accounting/ReportDetailPanel.vue'
import ReportDuplicateDialog from '@/components/accounting/ReportDuplicateDialog.vue'
import ReportRecomputeDialog from '@/components/accounting/ReportRecomputeDialog.vue'

const saveStateId = 'reports-grid-v2'

const i18n = getI18n()
const dataGridWidget = useDataGridWidget()
const accountingStore = useAccountingStore()
const webSiteStore = useWebSiteStore()
const { isMobile } = useDetectDevice()
const { getCurrencyString } = useCurrency()
const { subjectLabel, subjectChips } = useReportSubject()
const { gridRef } = useGridRef()

const editItem = ref(null)
const fileReport = ref(null)
const fileStartStep = ref(accountingStore.reportFileStepEnum.DOCUMENT)
const reportToDelete = ref(null)
const reportToDuplicate = ref(null)
const reportToRecompute = ref(null)

accountingStore.fetchCompanyDataAsync({}, true)

const gridDataSource = useODataSource(accountingStore.api.getAbsoluteUrl() + accountingStore.api.reportsUrl)
const referenceCurrencyCode = () => webSiteStore.getDefaultCurrency()?.code

const durationColumn = (dataField, caption) => ({
  dataField,
  caption: i18n.t(caption),
  dataType: 'number',
  allowFiltering: false,
  width: '7rem',
  calculateDisplayValue: (row) => formatDuration(row[dataField]),
})

const columns = [
  {
    type: 'buttons',
    buttons: [
      { hint: i18n.t('common.edit'), icon: 'edit', onClick: (e) => { editItem.value = { ...e.row.data } } },
      { hint: i18n.t('common.delete'), icon: 'trash', onClick: (e) => { reportToDelete.value = e.row.data } },
    ],
    fixed: false,
    visibleIndex: -1,
    showInColumnChooser: false,
    width: '5rem',
  },
  { dataField: 'name', caption: i18n.t('common.name'), width: '14rem' },
  {
    dataField: 'subject',
    caption: i18n.t('common.report.subjectKind'),
    width: '10rem',
    cellTemplate: 'subject-cell-template',
    lookup: { dataSource: accountingStore.getLocalizedReportSubjects(), valueExpr: 'id', displayExpr: 'name' },
  },
  { caption: i18n.t('common.report.subject'), name: 'subject_label', width: '12rem', calculateCellValue: (row) => subjectLabel(row) },
  { dataField: 'date_from', caption: i18n.t('common.report.period'), width: '14rem', dataType: 'date', sortOrder: 'desc', calculateDisplayValue: (row) => reportPeriodLabel(row) },
  {
    caption: i18n.t('common.report.totals'),
    name: 'totals',
    columns: [
      { dataField: 'total_days', caption: i18n.t('print.report.columns.days'), dataType: 'number', width: '5rem', allowFiltering: false },
      durationColumn('total_gross_minutes', 'print.report.columns.gross'),
      durationColumn('total_net_minutes', 'print.report.columns.net'),
      durationColumn('total_pause_minutes', 'print.report.columns.pause'),
      durationColumn('total_travel_minutes', 'print.report.columns.travel'),
      durationColumn('total_night_minutes', 'print.report.columns.night'),
      {
        dataField: 'total_expenses_reference',
        caption: i18n.t('common.report.expensesReference'),
        dataType: 'number',
        allowFiltering: false,
        width: '8rem',
        calculateDisplayValue: (row) => (row.total_expenses_reference ? getCurrencyString(+row.total_expenses_reference, referenceCurrencyCode()) : ''),
      },
    ],
  },
  { dataField: 'created_at', caption: i18n.t('common.report.createdOn'), dataType: 'datetime', width: '9rem' },
  { dataField: 'updated_at', caption: i18n.t('common.report.updatedOn'), dataType: 'datetime', width: '9rem' },
  { dataField: 'rows_computed_at', caption: i18n.t('common.report.dataAsOf'), dataType: 'datetime', width: '9rem', visible: false },
  {
    caption: '',
    name: 'pending',
    width: '3.5rem',
    allowFiltering: false,
    allowSorting: false,
    cellTemplate: 'pending-cell-template',
    calculateCellValue: (row) => (row.pending_changed_count || 0) + (row.pending_deleted_count || 0),
  },
]

const pendingTooltip = (row) => [
  row.pending_changed_count ? i18n.t('common.report.pendingChanged', { count: row.pending_changed_count }) : null,
  row.pending_deleted_count ? i18n.t('common.report.pendingDeleted', { count: row.pending_deleted_count }) : null,
].filter(Boolean).join(' · ')

const selectedOrNull = () => gridRef.value?.instance?.getSelectedRowsData()?.[0] ?? null

const openFile = (report, startStep = accountingStore.reportFileStepEnum.DOCUMENT) => {
  fileStartStep.value = startStep
  fileReport.value = report
}

const selectionButtons = { excel: ref(null), recompute: ref(null) }
const excelWidget = dataGridWidget.actionWidget(selectionButtons.excel, {
  icon: 'mdi mdi-microsoft-excel', text: i18n.t('common.exportToExcel'), onClick: () => openFile(selectedOrNull(), accountingStore.reportFileStepEnum.EXCEL),
})
const recomputeWidget = dataGridWidget.actionWidget(selectionButtons.recompute, {
  icon: 'mdi mdi-refresh', text: i18n.t('common.report.recompute'), onClick: () => { reportToRecompute.value = selectedOrNull() },
})

const setWidgetsDisabled = (disabled) => {
  [dataGridWidget.printWidgetButton, dataGridWidget.cloneWidgetButton, ...Object.values(selectionButtons)].forEach(button => button.value?.option('disabled', disabled))
}

const gridOptions = useDefaultGridOptions({
  id: saveStateId,
  saveStateId,
  dataSource: gridDataSource,
  columns,
  editing: { allowAdding: false, allowUpdating: false, allowDeleting: false },
  exportFileName: i18n.t('menu.accounting.reports'),
  clearFilterButton: true,
  selection: { mode: 'multiple', showCheckBoxesMode: 'onClick', selectAllMode: 'page' },
  onSelectionChanged: (e) => {
    setWidgetsDisabled(e.selectedRowsData.length !== 1)
  },
  exportWidgets: [
    dataGridWidget.printWidget({ onClick: () => openFile(selectedOrNull()) }),
    excelWidget,
    recomputeWidget,
    dataGridWidget.cloneWidget({ text: i18n.t('common.report.duplicate'), onClick: () => { reportToDuplicate.value = selectedOrNull() } }),
  ],
  addButton: { onClick: () => { editItem.value = {} } },
  masterDetail: { enabled: true, template: 'master-detail' },
})

const refreshAsync = (createdId = null) => refreshGridAsync(gridRef, createdId)

const onReportSaved = async(report) => {
  const isCreation = !editItem.value?.id
  editItem.value = null
  await refreshAsync(isCreation ? report.id : null)
}

const onDeleteConfirmed = async() => {
  await accountingStore.deleteReportAsync(reportToDelete.value.id)
  reportToDelete.value = null
  await refreshAsync()
}

const onDuplicated = async(id) => {
  reportToDuplicate.value = null
  await refreshAsync(id)
}

const onRecomputed = async() => {
  reportToRecompute.value = null
  await refreshAsync()
}

</script>

<template>
  <div>
    <confirm-dialog
      :model-value="reportToDelete !== null"
      :message="i18n.t('common.confirmDelete')"
      icon="mdi-delete-outline"
      icon-color="negative"
      @confirm="onDeleteConfirmed"
      @cancel="reportToDelete = null"
    />
    <report-duplicate-dialog
      v-if="reportToDuplicate"
      :report="reportToDuplicate"
      @duplicated="onDuplicated"
      @cancel="reportToDuplicate = null"
    />
    <report-recompute-dialog
      v-if="reportToRecompute"
      :report="reportToRecompute"
      @recomputed="onRecomputed"
      @cancel="reportToRecompute = null"
    />
    <q-dialog
      :model-value="editItem !== null"
      class="dialog-lg"
      :maximized="isMobile"
      persistent
    >
      <report-edit
        v-if="editItem !== null"
        :model-value="editItem"
        @update="onReportSaved"
        @cancel="editItem = null"
      />
    </q-dialog>
    <q-dialog :model-value="fileReport !== null" :maximized="isMobile" @hide="fileReport = null">
      <report-file-dialog
        v-if="fileReport !== null"
        :report="fileReport"
        :start-step="fileStartStep"
        @saved="refreshAsync()"
        @cancel="fileReport = null"
      />
    </q-dialog>
    <dx-data-grid ref="gridRef" v-bind="gridOptions">
      <template #subject-cell-template="{ data: templateOptions }">
        <chip-template :values="subjectChips(templateOptions.data.subject)" />
      </template>
      <template #pending-cell-template="{ data: templateOptions }">
        <div v-if="templateOptions.value" :class="`row items-center no-wrap text-weight-bold text-${signalToneColor(signalToneEnum.ATTENTION)}`">
          <q-icon name="mdi-alert-circle-outline" size="xs" />
          <span class="q-ml-xs">{{ templateOptions.value }}</span>
          <q-tooltip>{{ pendingTooltip(templateOptions.data) }}</q-tooltip>
        </div>
      </template>
      <template #master-detail="{ data: templateData }">
        <report-detail-panel :report="templateData.data" @recompute="reportToRecompute = templateData.data" />
      </template>
    </dx-data-grid>
  </div>
</template>
